import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch } from '@/app/store/store';
import { useGetProductDetailQuery } from '../../infra/adapters/pdp.adapter';
import {
  isPriceBreakupIncomplete,
  plainObjectToProductDetail,
} from '../../infra/mappers/pdp.mapper';
import {
  clearPdpInitialData,
  selectPdpInitialData,
  type PdpInitialState,
  PDP_INITIAL_SLICE_NAME,
} from '@/app/store/pdpInitialSlice';
import type { ProductDetail } from '../../domain/models/ProductDetail';

export interface UsePdpServiceOptions {
  designId: string;
}

export interface UsePdpServiceReturn {
  productDetail: ProductDetail | null;
  isLoading: boolean;
  error: unknown;
}

/** Survives PDP remounts so Back can restore a product after a similar-design 404. */
const pdpByDesignIdCache = new Map<string, ProductDetail>();

function rememberPdp(product: ProductDetail | null | undefined): void {
  if (!product) return;
  const id = String(product.designId);
  if (!id || id === '0') return;
  pdpByDesignIdCache.set(id, product);
}

function readCachedPdp(designId: string): ProductDetail | null {
  if (!designId) return null;
  return pdpByDesignIdCache.get(designId) ?? null;
}

function mapCachedPayload(
  cachedData: Record<string, unknown> | undefined,
  designId: string
): ProductDetail | null {
  if (!cachedData || typeof cachedData !== 'object') return null;
  try {
    const mapped = plainObjectToProductDetail(cachedData);
    if (String(mapped.designId) !== designId) return null;
    rememberPdp(mapped);
    return mapped;
  } catch {
    return null;
  }
}

/**
 * Reads PDP data from Redux/RTK cache.
 * SSR seeds the pdpInitial slice; client does not refetch on hydration.
 * Falls back to RTK Query cache for client-side navigations.
 */
export function usePdpService({ designId }: UsePdpServiceOptions): UsePdpServiceReturn {
  const dispatch = useDispatch<AppDispatch>();
  const ssrData = useSelector((state: { [PDP_INITIAL_SLICE_NAME]?: PdpInitialState }) =>
    selectPdpInitialData(state)
  );
  const ssrSnapshotRef = useRef<ProductDetail | null>(null);
  const [forceNetworkFetch, setForceNetworkFetch] = useState(false);

  if (ssrData !== null) {
    try {
      const ssrProduct = plainObjectToProductDetail(ssrData as unknown as Record<string, unknown>);
      rememberPdp(ssrProduct);
      if (String(ssrProduct.designId) === designId) {
        ssrSnapshotRef.current = ssrProduct;
      }
    } catch {
      // Ignore malformed SSR payloads; RTK fetch can recover.
    }
  }

  if (ssrSnapshotRef.current && String(ssrSnapshotRef.current.designId) !== designId) {
    ssrSnapshotRef.current = null;
    setForceNetworkFetch(false);
  }

  useEffect(() => {
    if (ssrSnapshotRef.current && isPriceBreakupIncomplete(ssrSnapshotRef.current)) {
      setForceNetworkFetch(true);
    }
    if (ssrData != null) {
      dispatch(clearPdpInitialData());
    }
    // Run once after first render to drop SSR data so remounts refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const skipRtk =
    !forceNetworkFetch &&
    ssrSnapshotRef.current !== null &&
    String(ssrSnapshotRef.current.designId) === designId;

  const {
    data: cachedData,
    isFetching,
    isError,
    isSuccess,
    error,
  } = useGetProductDetailQuery(
    { designId },
    { refetchOnMountOrArgChange: forceNetworkFetch, skip: skipRtk }
  );

  const productDetail = useMemo<ProductDetail | null>(() => {
    const snapshot = ssrSnapshotRef.current;
    if (!forceNetworkFetch && snapshot && String(snapshot.designId) === designId) {
      rememberPdp(snapshot);
      return snapshot;
    }
    const fromRtk = mapCachedPayload(cachedData as Record<string, unknown> | undefined, designId);
    if (fromRtk) return fromRtk;
    return readCachedPdp(designId);
  }, [cachedData, designId, forceNetworkFetch]);

  const querySettled = Boolean(isError) || (Boolean(isSuccess) && !isFetching);
  const resolvedNotFound = !productDetail && !skipRtk && querySettled;

  return {
    productDetail,
    isLoading: skipRtk ? false : !productDetail && !resolvedNotFound,
    error: skipRtk || !resolvedNotFound ? null : error,
  };
}
